import React, { useState, useEffect } from 'react';
import { Box, TextField, InputAdornment, IconButton, Paper, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

interface ChatMessage {
  text: string;
  timestamp: Date;
  isUser: boolean;
}

// Scripted responses with a more robust framework
const getScriptedResponse = (question: string): string | null => {
  // Convert question to lowercase for case-insensitive matching
  const normalizedQuestion = question;
  
  // Check for supplier spend question in various forms
  if (normalizedQuestion.includes("Which suppliers account for the highest portion of our spend, and how are they classified?") ) {
    return `Based on our analysis of the March 2025 spend data, the top 3 suppliers by spend are:

1. Alpha Tech Solutions ($3,950,000 - 22.7% of total spend)
   Classification: IT Equipment
   
2. Prime Logistics LLC ($2,270,000 - 17.4% of total spend)
   Classification: Frieght
   
3. Innovatech Industries ($1,530,000 - 15.3% of total spend)
   Classification: Professional Services

These 3 suppliers account for approximately $7,750,000 (77.5%) of your total organisational spend of $10,012,000.`;
  }
  
  // Add more question patterns and responses here
  if (normalizedQuestion === "Can you give me the top suppliers by transaction volume?") {
    return `Based on our analysis of the March 2025 spend data, the top 3 suppliers by spend are:

1. Apex Echo Systems (20,000 transactions)
   
2. Prime Logistics LLC (1,200 transactions)
   
3. Quantum Byte Industries (40 transactions)

These 3 suppliers account for approximately 21,240 (82.1%) of your total organisational transactions.`;
  }
  
  if (normalizedQuestion === "It appears we've identified a rate breach. Could you please verify the potential savings associated with this breach, and let us know if there are any actions required from your side?") {
    // Combine the responses into a single string instead of returning an array

    return `I've analysed the rate information for Prime Logistics LLC based on your March 2025 spend data.
      
ALERT: We've identified 1 potential rate breaches for Prime Logistics LLC:
      
1. Standard Freight Delivery: Charged at $3.50 per mile vs. contracted rate of $3.20
These breaches represent approximately $15,000 in potential overcharges for the reporting period. I recommend scheduling a supplier review meeting to address these discrepancies.`;
  }
  
  // Default response if no match
  return "I don't have specific information about that. Would you like to know about your top suppliers by spend, savings opportunities, or contract expirations?";
};

const ChatPage: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      // Add user message
      const userMessage: ChatMessage = {
        text: inputValue,
        timestamp: new Date(),
        isUser: true
      };
      
      setMessages(prevMessages => [...prevMessages, userMessage]);
      console.log('User asked:', inputValue);
      
      // Get scripted response
      const responseText = getScriptedResponse(inputValue);
      
      // Add system response with 6 second delay to simulate thinking
      setTimeout(() => {
        const systemMessage: ChatMessage = {
          text: responseText || "I don't understand that question.",
          timestamp: new Date(),
          isUser: false
        };
        
        setMessages(prevMessages => [...prevMessages, systemMessage]);
      }, 6000);
      
      setInputValue('');
    }
  };

  return (
    <Box sx={{ 
      height: '100%', 
      width: '100%', 
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end'
    }}>
      {/* User messages displayed in the same alignment as text input */}
      <Box sx={{ 
        position: 'absolute',
        top: 16,
        left: '10%',  // Align with text input (10% from left = 80% width centered)
        width: '80%',
        maxHeight: '70vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 2,
        pointerEvents: 'none' // So it doesn't interfere with page interactions
      }}>
        {messages.map((message, index) => (
          <Paper
            key={index}
            elevation={1}
            sx={{
              padding: '12px 20px',
              borderRadius: '12px',
              backgroundColor: message.isUser ? 'primary.main' : 'grey.100',
              color: message.isUser ? 'white' : 'text.primary',
              display: 'inline-block',
              maxWidth: '100%',
              wordBreak: 'break-word',
              pointerEvents: 'auto', // Make the messages themselves interactive
              marginBottom: 1,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              alignSelf: message.isUser ? 'flex-end' : 'flex-start',
              whiteSpace: 'pre-line' // Preserve line breaks in responses
            }}
          >
            <Typography variant="body1">
              {message.text}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                textAlign: 'right',
                opacity: 0.8,
                fontSize: '0.7rem',
                mt: 0.5
              }}
            >
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Paper>
        ))}
      </Box>
      
      {/* Ask anything input box at the bottom */}
      <Box 
        component="form"
        onSubmit={handleSubmit}
        sx={{ 
          position: 'relative',
          width: '80%',
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: '16px',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <TextField
          fullWidth
          placeholder="Ask anything..."
          value={inputValue}
          onChange={handleInputChange}
          variant="outlined"
          autoComplete="off"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  type="submit"
                  color="primary"
                  disabled={!inputValue.trim()}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: '24px',
              backgroundColor: '#fff',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '24px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              padding: '4px 8px',
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default ChatPage;